import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  Copy,
  CopyPlus,
  ExternalLink,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { apiDelete, apiGet, apiPost, ApiError } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { Button } from "../components/Button.js";
import { StatusPill } from "../components/StatusPill.js";
import { ActionMenu, ActionMenuItem } from "../components/ActionMenu.js";
import { DuplicateFormStrip } from "./DuplicateFormStrip.js";
import { RenameFormStrip } from "./RenameFormStrip.js";
import { useAuth } from "../auth/AuthContext.js";

interface FormSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  programmeId: string;
  cycleId: string | null;
  submissionCount: number;
  liveVersionId: string | null;
  publicUrl: string | null;
  updatedAt: string;
}

export const FormsPage = ({
  onOpen,
  onOpenSubmissions,
  onOpenAnalytics,
}: {
  onOpen: (formId: string) => void;
  onOpenSubmissions?: (form: { id: string; title: string }) => void;
  onOpenAnalytics?: (form: { id: string; title: string }) => void;
}): JSX.Element => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [programmeId, setProgrammeId] = useState("");

  const formsQuery = useQuery({
    queryKey: ["forms"],
    queryFn: () => apiGet<{ data: FormSummary[] }>("/forms"),
  });

  const programmesQuery = useQuery({
    queryKey: ["programmes"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/programmes"),
    retry: false,
  });

  const createForm = useMutation({
    mutationFn: (input: { programmeId: string; title: string; slug: string }) =>
      apiPost<{ id: string }>("/forms", input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["forms"] });
      setCreating(false);
      setTitle("");
      onOpen(created.id);
    },
  });

  const invalidateForms = () => queryClient.invalidateQueries({ queryKey: ["forms"] });

  const archive = useMutation({
    mutationFn: (formId: string) => apiPost(`/forms/${formId}/archive`),
    onSuccess: invalidateForms,
  });

  const unarchive = useMutation({
    mutationFn: (formId: string) => apiPost(`/forms/${formId}/unarchive`),
    onSuccess: invalidateForms,
  });

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const deleteForm = useMutation({
    mutationFn: (formId: string) => apiDelete(`/forms/${formId}`),
    onSuccess: () => {
      setConfirmingDelete(null);
      setDeleteError(null);
      return invalidateForms();
    },
    onError: (err) => {
      setDeleteError(err instanceof ApiError ? err.message : "Could not delete this form.");
    },
  });

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyLink = (formId: string, url: string): void => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(formId);
      setTimeout(() => setCopiedId((current) => (current === formId ? null : current)), 1500);
    });
  };

  const slugify = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);

  // Mirrors the server's `form.duplicate`, which is PROGRAMME_ADMIN-only and stricter than the
  // `form.edit_draft` an editor holds. The server remains the authority; this only decides whether
  // offering the control would be an empty promise.
  // `PATCH /forms/:id` gates on `form.edit_draft`, which an editor holds as well as a programme
  // admin — a wider set than duplicate, so it gets its own check rather than reusing that one.
  const canRename = (programmeId: string): boolean =>
    user?.isSuperadmin === true ||
    (user?.programmeRoles ?? []).some(
      (entry) =>
        entry.programmeId === programmeId &&
        (entry.role === "PROGRAMME_ADMIN" || entry.role === "EDITOR"),
    );

  const canDuplicate = (programmeId: string): boolean =>
    user?.isSuperadmin === true ||
    (user?.programmeRoles ?? []).some(
      (entry) => entry.programmeId === programmeId && entry.role === "PROGRAMME_ADMIN",
    );

  const forms = formsQuery.data?.data ?? [];
  const programmeName = (id: string): string =>
    (programmesQuery.data?.data ?? []).find((programme) => programme.id === id)?.name ?? "";

  return (
    <div className="space-y-5">
      <PageHeader
        title="Forms"
        description="Every application form across the three programmes. Open one to edit its questions, or go straight to its submissions."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating((current) => !current)}>
            New form
          </Button>
        }
      />

      {creating && (
        <form
          className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3 shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            if (!title || !programmeId) return;
            createForm.mutate({
              programmeId,
              title,
              slug: `${slugify(title)}-${Date.now().toString(36)}`,
            });
          }}
        >
          <label className="flex-1">
            <span className="mb-1 block text-[11px] uppercase text-muted-foreground">Title</span>
            <input
              className="w-full rounded-md border px-2 py-1.5 text-sm"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="2026/2027 Scholarship Application"
              required
            />
          </label>
          <label>
            <span className="mb-1 block text-[11px] uppercase text-muted-foreground">
              Programme
            </span>
            <select
              className="rounded-md border px-2 py-1.5 text-sm"
              value={programmeId}
              onChange={(event) => setProgrammeId(event.target.value)}
              required
            >
              <option value="">Select…</option>
              {(programmesQuery.data?.data ?? []).map((programme) => (
                <option key={programme.id} value={programme.id}>
                  {programme.name}
                </option>
              ))}
            </select>
          </label>
          <Button variant="primary" type="submit" disabled={createForm.isPending}>
            {createForm.isPending ? "Creating…" : "Create and open"}
          </Button>
          <Button variant="ghost" onClick={() => setCreating(false)}>
            Cancel
          </Button>
        </form>
      )}

      {formsQuery.isLoading && <p className="text-sm text-muted-foreground">Loading forms…</p>}

      {deleteError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {deleteError}
        </p>
      )}

      {!formsQuery.isLoading && forms.length === 0 && (
        <EmptyState
          illustration="/illustrations/empty-explore.webp"
          icon={FileText}
          title="No forms yet"
          description="A form is where applicants answer your questions. Create one, add its fields, then publish it to get a public link you can share."
          action={
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Create the first form
            </Button>
          }
        />
      )}

      <div className="space-y-2">
        {forms.map((form) => (
          <article
            key={form.id}
            className="group rounded-lg border bg-card shadow-sm transition-colors hover:border-primary/50"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 p-4">
              {/* The title is the primary target: opening the builder is what you came here to do. */}
              <button
                type="button"
                onClick={() => onOpen(form.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-semibold group-hover:text-primary">
                    {form.title}
                  </span>
                  <StatusPill status={form.status} />
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                  {programmeName(form.programmeId) && (
                    <span>{programmeName(form.programmeId)}</span>
                  )}
                  <span aria-hidden>·</span>
                  <code>/f/{form.slug}</code>
                </span>
              </button>

              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpenSubmissions?.({ id: form.id, title: form.title })}
                >
                  <strong className="tabular-nums">{form.submissionCount.toLocaleString()}</strong>
                  {form.submissionCount === 1 ? "submission" : "submissions"}
                </Button>

                {form.status === "PUBLISHED" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={BarChart3}
                    onClick={() => onOpenAnalytics?.({ id: form.id, title: form.title })}
                  >
                    Analytics
                  </Button>
                )}

                {form.publicUrl && (
                  <>
                    <Button
                      size="sm"
                      icon={Copy}
                      onClick={() => copyLink(form.id, form.publicUrl!)}
                    >
                      {copiedId === form.id ? "Copied" : "Copy link"}
                    </Button>
                    <a
                      href={form.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs font-medium hover:bg-muted"
                    >
                      <ExternalLink aria-hidden className="h-3 w-3" />
                      Open
                    </a>
                  </>
                )}

                <ActionMenu label={`Actions for ${form.title}`}>
                  {canRename(form.programmeId) && (
                    <ActionMenuItem
                      onClick={() => {
                        setConfirmingDelete(null);
                        setDuplicating(null);
                        setRenaming(form.id);
                      }}
                      title="Changes the title applicants see. The web address stays the same"
                    >
                      <Pencil aria-hidden className="h-3.5 w-3.5" />
                      Rename…
                    </ActionMenuItem>
                  )}
                  {canDuplicate(form.programmeId) && (
                    <ActionMenuItem
                      onClick={() => {
                        setConfirmingDelete(null);
                        setRenaming(null);
                        setDuplicating(form.id);
                      }}
                      title="Copies the questions and settings into a new draft, without the submissions"
                    >
                      <CopyPlus aria-hidden className="h-3.5 w-3.5" />
                      Duplicate…
                    </ActionMenuItem>
                  )}
                  {form.status === "ARCHIVED" ? (
                    <ActionMenuItem
                      onClick={() => unarchive.mutate(form.id)}
                      disabled={unarchive.isPending}
                      title="Returns the form to Draft"
                    >
                      <ArchiveRestore aria-hidden className="h-3.5 w-3.5" />
                      Unarchive
                    </ActionMenuItem>
                  ) : (
                    <ActionMenuItem
                      onClick={() => archive.mutate(form.id)}
                      disabled={archive.isPending}
                      title="Closes the form but keeps it and its submissions for reference"
                    >
                      <Archive aria-hidden className="h-3.5 w-3.5" />
                      Archive
                    </ActionMenuItem>
                  )}
                  <ActionMenuItem
                    tone="danger"
                    onClick={() => {
                      setDeleteError(null);
                      setDuplicating(null);
                      setRenaming(null);
                      setConfirmingDelete(form.id);
                    }}
                    title="A form that already has submissions can only be deleted by a superadmin"
                  >
                    <Trash2 aria-hidden className="h-3.5 w-3.5" />
                    Delete…
                  </ActionMenuItem>
                </ActionMenu>
              </div>
            </div>

            {renaming === form.id && (
              <RenameFormStrip
                form={form}
                onCancel={() => setRenaming(null)}
                onRenamed={() => setRenaming(null)}
              />
            )}

            {duplicating === form.id && (
              <DuplicateFormStrip
                form={form}
                onCancel={() => setDuplicating(null)}
                onDuplicated={(newFormId) => {
                  setDuplicating(null);
                  onOpen(newFormId);
                }}
              />
            )}

            {confirmingDelete === form.id && (
              <div className="flex flex-wrap items-center gap-2 border-t border-destructive/30 bg-destructive/5 px-4 py-2.5 text-xs">
                <span className="text-destructive">
                  Delete <strong>{form.title}</strong> permanently?
                  {form.submissionCount > 0 &&
                    ` Its ${form.submissionCount.toLocaleString()} submissions go with it.`}
                </span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => deleteForm.mutate(form.id)}
                  disabled={deleteForm.isPending}
                >
                  {deleteForm.isPending ? "Deleting…" : "Yes, delete it"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(null)}>
                  Cancel
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
};
